/**
 * Active ledger session — multi-book within one organization.
 */

/** Keep in sync with workspaceStore / cloudSync event name. */
const WORKSPACE_DATA_EVENT = 'workspace-data';

export const LEDGER_LIST_KEY = 'ef_ledgers_v1';
export const ACTIVE_LEDGER_KEY = 'ef_active_ledger_v1';
export const VIEW_ALL_LEDGERS_KEY = 'ef_view_all_ledgers_v1';

export type LedgerPublic = {
  id: string;
  _id?: string;
  name: string;
  slug?: string;
  isDefault: boolean;
  isDisabled: boolean;
  hasLogin?: boolean;
  loginEmail?: string | null;
  /** Present for owners/admins — delete is only allowed when 0. */
  recordCount?: number;
  /** Sources shown on this ledger's consolidated Reports summary (ignored for the Main Account). */
  reportConsolidation?: { includeMain: boolean; ledgerIds: string[] };
};

export function getLedgerList(): LedgerPublic[] {
  try {
    const raw = localStorage.getItem(LEDGER_LIST_KEY);
    return raw ? (JSON.parse(raw) as LedgerPublic[]) : [];
  } catch {
    return [];
  }
}

export function setLedgerList(ledgers: LedgerPublic[]) {
  try {
    localStorage.setItem(LEDGER_LIST_KEY, JSON.stringify(ledgers));
  } catch {
    /* ignore */
  }
}

export function getActiveLedgerId(): string | null {
  try {
    return localStorage.getItem(ACTIVE_LEDGER_KEY);
  } catch {
    return null;
  }
}

export function setActiveLedgerId(id: string | null) {
  try {
    if (id) localStorage.setItem(ACTIVE_LEDGER_KEY, id);
    else localStorage.removeItem(ACTIVE_LEDGER_KEY);
  } catch {
    /* ignore */
  }
}

export function isViewAllLedgers(): boolean {
  try {
    return localStorage.getItem(VIEW_ALL_LEDGERS_KEY) === '1';
  } catch {
    return false;
  }
}

export function setViewAllLedgers(on: boolean) {
  try {
    if (on) localStorage.setItem(VIEW_ALL_LEDGERS_KEY, '1');
    else localStorage.removeItem(VIEW_ALL_LEDGERS_KEY);
  } catch {
    /* ignore */
  }
}

export function getActiveLedger(): LedgerPublic | null {
  const id = getActiveLedgerId();
  if (!id) return getLedgerList().find((l) => l.isDefault) || getLedgerList()[0] || null;
  return getLedgerList().find((l) => l.id === id) || null;
}

/** Apply auth/login response ledger metadata. */
export function applyAuthLedgers(payload: {
  ledgers?: LedgerPublic[];
  defaultLedgerId?: string | null;
  activeLedgerId?: string | null;
}) {
  if (payload.ledgers?.length) setLedgerList(payload.ledgers);
  const active = payload.activeLedgerId || payload.defaultLedgerId;
  if (active) setActiveLedgerId(active);
  setViewAllLedgers(false);
}

export async function switchLedger(
  ledgerId: string | null,
  opts: { viewAll?: boolean; onSwitch?: () => Promise<void> } = {}
) {
  if (opts.viewAll) {
    setViewAllLedgers(true);
    setActiveLedgerId(null);
  } else if (ledgerId) {
    setViewAllLedgers(false);
    setActiveLedgerId(ledgerId);
  }
  if (opts.onSwitch) await opts.onSwitch();
  window.dispatchEvent(new Event(WORKSPACE_DATA_EVENT));
}

export function canManageLedgers(user?: { isOrgOwner?: boolean; deskRole?: string; role?: string } | null): boolean {
  if (!user) return false;
  if (user.role === 'SUPER_ADMIN') return true;
  if (user.isOrgOwner) return true;
  const role = user.deskRole || 'admin';
  return role === 'super_admin' || role === 'admin';
}

export function deskRoleLabel(user?: { isOrgOwner?: boolean; deskRole?: string } | null): string {
  if (!user) return 'Staff';
  if (user.isOrgOwner) return 'Owner';
  const role = user.deskRole || 'admin';
  if (role === 'super_admin') return 'Admin';
  if (role === 'admin') return 'Manager';
  if (role === 'ledger_desk') return 'Branch desk';
  return 'Staff';
}
